import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack,
  OrderedList, ListItem, Divider, Button, Link, SimpleGrid, Icon,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight, FiRefreshCw, FiGithub, FiShield, FiGlobe, FiUsers, FiGrid } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const LaunchSiteManagement: React.FC = () => {
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          {/* Back link */}
          <Link as={RouterLink} to="/updates" _hover={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color="gray.500">
              All Updates
            </Button>
          </Link>

          {/* Header */}
          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="purple">Launch Module</Badge>
              <Badge colorScheme="blue">v1.1.0</Badge>
              <Text fontSize="xs" color="gray.400">24 Mar 2026</Text>
            </HStack>
            <Heading size="lg" color="#2c5282">Site Management Upgrade</Heading>
            <Text color="gray.600">
              Manage your deployed sites without leaving Business Builder. Rebuild, add collaborators, configure domains, and bootstrap admin access — all from one page.
            </Text>
          </VStack>

          <Divider />

          {/* What's New */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's New</Heading>
            <Text color="gray.600" mb={4}>
              The Launch module now goes beyond deployment. Once a site is live, you can manage every aspect of it directly from the site detail page — no need to log into Vercel, GitHub, or any other service separately.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="purple.200" bg="purple.50">
                <HStack mb={2}>
                  <Icon as={FiRefreshCw} color="purple.500" />
                  <Text fontWeight="bold" color="purple.700">One-Click Rebuild</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Trigger a Vercel redeployment with one button. Pulls the latest code from GitHub and rebuilds — no paid Vercel account needed.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="gray.200" bg="gray.50">
                <HStack mb={2}>
                  <Icon as={FiGithub} color="gray.700" />
                  <Text fontWeight="bold" color="gray.700">GitHub Collaborators</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Add or remove GitHub usernames as collaborators on deployed site repos. They get push access to contribute code.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="green.200" bg="green.50">
                <HStack mb={2}>
                  <Icon as={FiShield} color="green.500" />
                  <Text fontWeight="bold" color="green.700">Admin Bootstrap</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Add phone numbers and emails that automatically get admin access on deployed tenant sites. Perfect for onboarding new site admins.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="blue.200" bg="blue.50">
                <HStack mb={2}>
                  <Icon as={FiGlobe} color="blue.500" />
                  <Text fontWeight="bold" color="blue.700">Domain Management</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Add, remove, and view custom domains with verified and configured status badges. DNS configuration guidance included.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="orange.200" bg="orange.50">
                <HStack mb={2}>
                  <Icon as={FiUsers} color="orange.500" />
                  <Text fontWeight="bold" color="orange.700">Multi-Tenant Ready</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Launch module can now be deployed to other tenant sites. Each tenant only sees sites launched from their own platform.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="teal.200" bg="teal.50">
                <HStack mb={2}>
                  <Icon as={FiGrid} color="teal.500" />
                  <Text fontWeight="bold" color="teal.700">Dashboard Upgrade</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  New "Deployed By" column in the sites table so you can see who launched each site at a glance.
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* How to Use */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>How to Use</Heading>
            <OrderedList spacing={3} pl={4}>
              <ListItem>
                <Text color="gray.600">Go to the <strong>Launch</strong> module and click into any deployed site.</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">Click <strong>Rebuild</strong> to trigger a fresh deployment from the latest code.</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">Scroll to <strong>Domain Management</strong> to add or remove custom domains.</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">Under <strong>GitHub Collaborators</strong>, add team members by their GitHub username.</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">In <strong>Admin Access</strong>, add phone numbers or emails to grant admin rights on the tenant site.</Text>
              </ListItem>
            </OrderedList>
          </Box>

          <Divider />

          {/* Try It Now */}
          <Box bg="green.50" p={5} borderRadius="md" border="1px solid" borderColor="green.200">
            <Heading size="md" color="#38a169" mb={3}>Try It Now!</Heading>
            <Text mb={3} color="gray.600">Head to the Launch module to manage your deployed sites.</Text>
            <Link as={RouterLink} to="/launch" _hover={{ textDecoration: "none" }}>
              <Button colorScheme="green" size="md" rightIcon={<FiArrowRight />}>
                Open Launch Module
              </Button>
            </Link>
          </Box>

          {/* Sign off */}
          <Box>
            <Text color="gray.600">As always, reach out if you have any questions or feedback.</Text>
            <Text mt={4} color="gray.600">
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color="gray.500">Business Builder</Text>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default LaunchSiteManagement;
